import React, { useEffect, useState } from 'react';
import { ShoppingBagIcon, ChevronLeftIcon, PlusIcon, TagIcon, PackageIcon, ImageIcon, SearchIcon, FilterIcon, LayoutGridIcon, ListIcon, QrCodeIcon, LinkIcon, CopyIcon, TrashIcon, EditIcon, CheckIcon, XIcon, ArchiveIcon, EyeIcon, DollarSignIcon, BarChartIcon } from 'lucide-react';
import { Shop } from './Shops';
import { ShopDashboard } from '../components/ShopDashboard';
// Product type definition
interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  discountedPrice?: number;
  categoryId: string;
  imageUrl: string;
  stockQuantity: number;
  sku: string;
  isActive: boolean;
}
// Category type definition
interface Category {
  id: string;
  name: string;
  description: string;
  productCount: number;
}
export function ShopDetails() {
  // Get shop ID from URL parameter
  const getShopId = () => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('id');
    }
    return null;
  };
  const isNewShop = () => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('new') === 'true';
    }
    return false;
  };
  // Shop state
  const [shop, setShop] = useState<Shop | null>(null);
  const [isEditing, setIsEditing] = useState(isNewShop());
  const [activeTab, setActiveTab] = useState<'dashboard' | 'products' | 'categories' | 'settings'>('dashboard');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | 'all'>('all');
  // Products and categories state
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  // Form state for new shop
  const [shopForm, setShopForm] = useState({
    name: '',
    description: '',
    logo: '•',
    color: '#f8d7d7',
    channels: ['ecommerce']
  });
  // Mock data loading
  useEffect(() => {
    const shopId = getShopId();
    if (shopId && !isNewShop()) {
      // Mock shop data
      const mockShop: Shop = {
        id: shopId,
        name: 'Smart Mobile Hub',
        description: 'Latest smartphones and mobile accessories',
        logo: '📱',
        color: '#e3f2fd',
        isOpen: true,
        channels: ['pos', 'ecommerce'],
        productsCount: 48,
        revenue: {
          total: 125800,
          currency: 'MYR',
          growth: 15.3
        },
        ordersCount: 124
      };
      setShop(mockShop);
      setShopForm({
        name: mockShop.name,
        description: mockShop.description,
        logo: mockShop.logo,
        color: mockShop.color,
        channels: [...mockShop.channels]
      });
      // Mock categories
      setCategories([{
        id: 'cat1',
        name: 'Mobile Phones',
        description: 'Smartphones and feature phones',
        productCount: 15
      }, {
        id: 'cat2',
        name: 'Mobile Accessories',
        description: 'Cases, chargers, and cables',
        productCount: 18
      }, {
        id: 'cat3',
        name: 'Tablets',
        description: 'iPads and Android tablets',
        productCount: 8
      }, {
        id: 'cat4',
        name: 'Wearables',
        description: 'Smartwatches and fitness trackers',
        productCount: 7
      }]);
      // Mock products
      setProducts([{
        id: 'prod1',
        name: 'iPhone 15 Pro',
        description: 'Latest flagship smartphone with A17 Pro chip',
        price: 999.99,
        categoryId: 'cat1',
        imageUrl: 'https://images.unsplash.com/photo-1592286927505-b0c2e0a13e60?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 25,
        sku: 'IP15P-001',
        isActive: true
      }, {
        id: 'prod2',
        name: 'Samsung Galaxy S24',
        description: 'Powerful Android flagship with AI features',
        price: 899.99,
        discountedPrice: 849.99,
        categoryId: 'cat1',
        imageUrl: 'https://images.unsplash.com/photo-1610945415295-d9bbf067e59c?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 32,
        sku: 'SGS24-002',
        isActive: true
      }, {
        id: 'prod3',
        name: 'Wireless Charging Pad',
        description: 'Fast wireless charger for all Qi-enabled devices',
        price: 39.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1591290619762-c588f7e8e86f?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 50,
        sku: 'WCP-003',
        isActive: true
      }, {
        id: 'prod4',
        name: 'Apple Watch Ultra 2',
        description: 'Advanced fitness and health tracking smartwatch',
        price: 799.99,
        categoryId: 'cat4',
        imageUrl: 'https://images.unsplash.com/photo-1579586337278-3befd40fd17a?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 18,
        sku: 'AWU2-004',
        isActive: true
      }, {
        id: 'prod5',
        name: 'iPad Pro 12.9"',
        description: 'Powerful tablet with M2 chip and ProMotion display',
        price: 1099.99,
        categoryId: 'cat3',
        imageUrl: 'https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 15,
        sku: 'IPP12-005',
        isActive: true
      }, {
        id: 'prod6',
        name: 'USB-C Fast Charger',
        description: '65W fast charging adapter with USB-C cable',
        price: 49.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1583863788434-e58a36330cf0?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 65,
        sku: 'USBC-006',
        isActive: true
      }]);
    }
  }, []);
  // Filter products based on search and category
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) || product.description.toLowerCase().includes(searchTerm.toLowerCase()) || product.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategoryId === 'all' || product.categoryId === selectedCategoryId;
    return matchesSearch && matchesCategory;
  });
  // Save shop
  const saveShop = () => {
    if (isNewShop()) {
      // Logic to create new shop
      window.location.href = '/shops';
    } else {
      // Logic to update shop
      setIsEditing(false);
    }
  };
  // Navigate back to shops
  const navigateToShops = () => {
    window.location.href = '/shops';
  };
  // Toggle product active status
  const toggleProductStatus = (productId: string) => {
    setProducts(products.map(product => product.id === productId ? {
      ...product,
      isActive: !product.isActive
    } : product));
  };
  // Render shop form
  const renderShopForm = () => <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-8622">
      <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-8623">
        {isNewShop() ? 'Create New Shop' : 'Edit Shop'}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-8624">
        <div data-id="element-8625">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8626">
            Shop Name *
          </label>
          <input type="text" value={shopForm.name} onChange={e => setShopForm({
          ...shopForm,
          name: e.target.value
        })} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Enter shop name" required data-id="element-8627" />
        </div>
        <div data-id="element-8628">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8629">
            Shop Logo
          </label>
          <input type="text" value={shopForm.logo} onChange={e => setShopForm({
          ...shopForm,
          logo: e.target.value
        })} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Logo character or emoji" maxLength={2} data-id="element-8630" />
          <p className="mt-1 text-xs text-gray-500" data-id="element-8631">
            Enter a single character or emoji to use as shop logo
          </p>
        </div>
        <div className="col-span-2" data-id="element-8632">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8633">
            Description
          </label>
          <textarea value={shopForm.description} onChange={e => setShopForm({
          ...shopForm,
          description: e.target.value
        })} rows={3} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Describe your shop" data-id="element-8634" />
        </div>
        <div data-id="element-8635">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8636">
            Logo Background Color
          </label>
          <div className="flex items-center" data-id="element-8637">
            <input type="color" value={shopForm.color} onChange={e => setShopForm({
            ...shopForm,
            color: e.target.value
          })} className="w-10 h-10 border-0 p-0" data-id="element-8638" />
            <input type="text" value={shopForm.color} onChange={e => setShopForm({
            ...shopForm,
            color: e.target.value
          })} className="block ml-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-8639" />
          </div>
        </div>
        <div data-id="element-8640">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8641">
            Shop Channels
          </label>
          <div className="space-y-2" data-id="element-8642">
            <label className="flex items-center" data-id="element-8643">
              <input type="checkbox" checked={shopForm.channels.includes('ecommerce')} onChange={e => {
              const newChannels = e.target.checked ? [...shopForm.channels, 'ecommerce'] : shopForm.channels.filter(c => c !== 'ecommerce');
              setShopForm({
                ...shopForm,
                channels: newChannels as ('pos' | 'ecommerce')[]
              });
            }} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-8644" />
              <span className="ml-2 text-sm text-gray-700" data-id="element-8645">
                E-Commerce (Online Shop)
              </span>
            </label>
            <label className="flex items-center" data-id="element-8646">
              <input type="checkbox" checked={shopForm.channels.includes('pos')} onChange={e => {
              const newChannels = e.target.checked ? [...shopForm.channels, 'pos'] : shopForm.channels.filter(c => c !== 'pos');
              setShopForm({
                ...shopForm,
                channels: newChannels as ('pos' | 'ecommerce')[]
              });
            }} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-8647" />
              <span className="ml-2 text-sm text-gray-700" data-id="element-8648">
                Point of Sale (Physical Store)
              </span>
            </label>
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-end space-x-3" data-id="element-8649">
        <button onClick={navigateToShops} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-8650">
          Cancel
        </button>
        <button onClick={saveShop} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium" data-id="element-8651">
          {isNewShop() ? 'Create Shop' : 'Save Changes'}
        </button>
      </div>
    </div>;
  // Render shop header
  const renderShopHeader = () => <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-8652">
      <div className="flex items-start justify-between" data-id="element-8653">
        <div className="flex items-start space-x-4" data-id="element-8654">
          <div className="w-16 h-16 rounded-md flex items-center justify-center flex-shrink-0" style={{
          backgroundColor: shop?.color
        }} data-id="element-8655">
            <span className="text-2xl font-bold text-red-600" data-id="element-8656">
              {shop?.logo}
            </span>
          </div>
          <div data-id="element-8657">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-8658">{shop?.name}</h2>
            <p className="text-gray-500 mt-1" data-id="element-8659">{shop?.description}</p>
            <div className="flex items-center mt-2 space-x-3" data-id="element-8660">
              {/* Shop channels */}
              {shop?.channels.includes('pos') && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800" data-id="element-8661">
                  POS
                </span>}
              {shop?.channels.includes('ecommerce') && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-8662">
                  E-Commerce
                </span>}
              {/* Shop status */}
              <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${shop?.isOpen ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-8663">
                {shop?.isOpen ? 'Open' : 'Closed'}
              </span>
            </div>
          </div>
        </div>
        {/* Shop actions */}
        <div className="flex items-center space-x-2" data-id="element-8664">
          <button onClick={() => setIsEditing(true)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md" title="Edit Shop" data-id="element-8665">
            <EditIcon size={18} data-id="element-8666" />
          </button>
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="QR Code" data-id="element-8667">
            <QrCodeIcon size={18} data-id="element-8668" />
          </button>
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Shop Link" data-id="element-8669">
            <LinkIcon size={18} data-id="element-8670" />
          </button>
        </div>
      </div>
      {/* Shop statistics */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-gray-200 pt-4" data-id="element-8671">
        <div className="flex items-center" data-id="element-8672">
          <div className="p-2 bg-blue-50 rounded-md mr-3" data-id="element-8673">
            <PackageIcon size={18} className="text-blue-600" data-id="element-8674" />
          </div>
          <div data-id="element-8675">
            <p className="text-sm text-gray-500" data-id="element-8676">Products</p>
            <p className="text-lg font-medium" data-id="element-8677">{shop?.productsCount}</p>
          </div>
        </div>
        <div className="flex items-center" data-id="element-8678">
          <div className="p-2 bg-green-50 rounded-md mr-3" data-id="element-8679">
            <DollarSignIcon size={18} className="text-green-600" data-id="element-8680" />
          </div>
          <div data-id="element-8681">
            <p className="text-sm text-gray-500" data-id="element-8682">Revenue</p>
            <div className="flex items-center" data-id="element-8683">
              <p className="text-lg font-medium" data-id="element-8684">
                RM{shop?.revenue.total.toLocaleString()}
              </p>
              <span className={`ml-2 text-xs ${shop?.revenue.growth && shop.revenue.growth >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-8685">
                {shop?.revenue.growth && shop.revenue.growth >= 0 ? '+' : ''}
                {shop?.revenue.growth}%
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center" data-id="element-8686">
          <div className="p-2 bg-purple-50 rounded-md mr-3" data-id="element-8687">
            <ShoppingBagIcon size={18} className="text-purple-600" data-id="element-8688" />
          </div>
          <div data-id="element-8689">
            <p className="text-sm text-gray-500" data-id="element-8690">Orders</p>
            <p className="text-lg font-medium" data-id="element-8691">{shop?.ordersCount}</p>
          </div>
        </div>
      </div>
    </div>;
  // Render tabs
  const renderTabs = () => <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-8692">
      <div className="flex border-b border-gray-200" data-id="element-8693">
        <button onClick={() => setActiveTab('dashboard')} className={`pb-4 px-1 mr-8 text-sm font-medium ${activeTab === 'dashboard' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-8694">
          Dashboard
        </button>
        <button onClick={() => setActiveTab('products')} className={`pb-4 px-1 mr-8 text-sm font-medium ${activeTab === 'products' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-8695">
          Products
        </button>
        <button onClick={() => setActiveTab('categories')} className={`pb-4 px-1 mr-8 text-sm font-medium ${activeTab === 'categories' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-8696">
          Categories
        </button>
        <button onClick={() => setActiveTab('settings')} className={`pb-4 px-1 text-sm font-medium ${activeTab === 'settings' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-8697">
          Shop Settings
        </button>
      </div>
    </div>;
  // Add a new renderDashboardTab function
  const renderDashboardTab = () => {
    if (!shop) return null;
    return <ShopDashboard shop={shop} data-id="element-8698" />;
  };
  // Render products tab
  const renderProductsTab = () => <div className="space-y-4" data-id="element-8699">
      {/* Products toolbar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-8700">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0" data-id="element-8701">
          <div className="flex-1 w-full md:w-auto" data-id="element-8702">
            <div className="relative" data-id="element-8703">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8704">
                <SearchIcon size={16} className="text-gray-400" data-id="element-8705" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Search products by name, description or SKU" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-8706" />
            </div>
          </div>
          <div className="flex space-x-2 w-full md:w-auto" data-id="element-8707">
            <div className="relative flex-grow md:flex-grow-0" data-id="element-8708">
              <select className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" value={selectedCategoryId} onChange={e => setSelectedCategoryId(e.target.value)} data-id="element-8709">
                <option value="all" data-id="element-8710">All Categories</option>
                {categories.map(category => <option key={category.id} value={category.id} data-id="element-8711">
                    {category.name}
                  </option>)}
              </select>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-8712">
                <FilterIcon size={16} className="text-gray-400" data-id="element-8713" />
              </div>
            </div>
            <div className="flex space-x-1" data-id="element-8714">
              <button onClick={() => setViewMode('grid')} className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gray-200' : 'hover:bg-gray-100'}`} data-id="element-8715">
                <LayoutGridIcon size={18} className="text-gray-700" data-id="element-8716" />
              </button>
              <button onClick={() => setViewMode('list')} className={`p-2 rounded ${viewMode === 'list' ? 'bg-gray-200' : 'hover:bg-gray-100'}`} data-id="element-8717">
                <ListIcon size={18} className="text-gray-700" data-id="element-8718" />
              </button>
            </div>
            <button className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium flex items-center" data-id="element-8719">
              <PlusIcon size={16} className="mr-1" data-id="element-8720" />
              Add Product
            </button>
          </div>
        </div>
      </div>
      {/* Products list */}
      {viewMode === 'grid' ? <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-8721">
          {filteredProducts.map(product => <div key={product.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-8722">
              <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden bg-gray-200" data-id="element-8723">
                <img src={product.imageUrl} alt={product.name} className="w-full h-48 object-cover" data-id="element-8724" />
                {!product.isActive && <div className="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center" data-id="element-8725">
                    <span className="px-2 py-1 bg-red-600 text-white text-xs font-medium rounded" data-id="element-8726">
                      Inactive
                    </span>
                  </div>}
              </div>
              <div className="p-4" data-id="element-8727">
                <h3 className="text-sm font-medium text-gray-900 truncate" data-id="element-8728">
                  {product.name}
                </h3>
                <p className="text-xs text-gray-500 mt-1 truncate" data-id="element-8729">
                  {product.description}
                </p>
                <div className="mt-2 flex justify-between items-center" data-id="element-8730">
                  <div data-id="element-8731">
                    {product.discountedPrice ? <div className="flex items-center" data-id="element-8732">
                        <span className="text-sm font-medium text-gray-900" data-id="element-8733">
                          RM{product.discountedPrice.toFixed(2)}
                        </span>
                        <span className="ml-2 text-xs text-gray-500 line-through" data-id="element-8734">
                          RM{product.price.toFixed(2)}
                        </span>
                      </div> : <span className="text-sm font-medium text-gray-900" data-id="element-8735">
                        RM{product.price.toFixed(2)}
                      </span>}
                  </div>
                  <span className="text-xs text-gray-500" data-id="element-8736">
                    Stock: {product.stockQuantity}
                  </span>
                </div>
                <div className="mt-3 flex justify-between items-center" data-id="element-8737">
                  <span className="text-xs text-gray-500" data-id="element-8738">
                    SKU: {product.sku}
                  </span>
                  <div className="flex space-x-1" data-id="element-8739">
                    <button onClick={() => toggleProductStatus(product.id)} className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded" data-id="element-8740">
                      {product.isActive ? <EyeIcon size={16} data-id="element-8741" /> : <EyeIcon size={16} data-id="element-8742" />}
                    </button>
                    <button className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded" data-id="element-8743">
                      <EditIcon size={16} data-id="element-8744" />
                    </button>
                    <button className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded" data-id="element-8745">
                      <TrashIcon size={16} data-id="element-8746" />
                    </button>
                  </div>
                </div>
              </div>
            </div>)}
          {/* Add product card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 border-dashed flex items-center justify-center h-64" data-id="element-8747">
            <button className="flex flex-col items-center text-gray-500 hover:text-blue-600 p-4" data-id="element-8748">
              <PlusIcon size={24} className="mb-2" data-id="element-8749" />
              <span className="text-sm font-medium" data-id="element-8750">Add Product</span>
            </button>
          </div>
        </div> : <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-8751">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-8752">
            <thead className="bg-gray-50" data-id="element-8753">
              <tr data-id="element-8754">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8755">
                  Product
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8756">
                  Category
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8757">
                  Price
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8758">
                  Stock
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8759">
                  SKU
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8760">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8761">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-8762">
              {filteredProducts.map(product => {
            const category = categories.find(c => c.id === product.categoryId);
            return <tr key={product.id} className="hover:bg-gray-50" data-id="element-8763">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-8764">
                      <div className="flex items-center" data-id="element-8765">
                        <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded bg-gray-100" data-id="element-8766">
                          <img src={product.imageUrl} alt={product.name} className="h-10 w-10 object-cover" data-id="element-8767" />
                        </div>
                        <div className="ml-4" data-id="element-8768">
                          <div className="text-sm font-medium text-gray-900" data-id="element-8769">
                            {product.name}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs" data-id="element-8770">
                            {product.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-8771">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-8772">
                        {category?.name || 'Uncategorized'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-8773">
                      {product.discountedPrice ? <div data-id="element-8774">
                          <span className="text-sm font-medium text-gray-900" data-id="element-8775">
                            RM{product.discountedPrice.toFixed(2)}
                          </span>
                          <span className="ml-2 text-xs text-gray-500 line-through" data-id="element-8776">
                            RM{product.price.toFixed(2)}
                          </span>
                        </div> : <span className="text-sm font-medium text-gray-900" data-id="element-8777">
                          RM{product.price.toFixed(2)}
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-8778">
                      <span className={`text-sm ${product.stockQuantity < 10 ? 'text-red-600 font-medium' : 'text-gray-900'}`} data-id="element-8779">
                        {product.stockQuantity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-8780">
                      {product.sku}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-8781">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${product.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-8782">
                        {product.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-8783">
                      <div className="flex justify-end space-x-2" data-id="element-8784">
                        <button onClick={() => toggleProductStatus(product.id)} className="text-gray-500 hover:text-blue-600" data-id="element-8785">
                          {product.isActive ? <EyeIcon size={16} data-id="element-8786" /> : <EyeIcon size={16} data-id="element-8787" />}
                        </button>
                        <button className="text-gray-500 hover:text-blue-600" data-id="element-8788">
                          <EditIcon size={16} data-id="element-8789" />
                        </button>
                        <button className="text-gray-500 hover:text-red-600" data-id="element-8790">
                          <TrashIcon size={16} data-id="element-8791" />
                        </button>
                      </div>
                    </td>
                  </tr>;
          })}
            </tbody>
          </table>
        </div>}
      {/* Empty state */}
      {filteredProducts.length === 0 && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-8792">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-8793">
            <PackageIcon size={24} className="text-blue-600" data-id="element-8794" />
          </div>
          <h3 className="text-lg font-medium text-gray-900" data-id="element-8795">
            No products found
          </h3>
          <p className="text-gray-500 mt-2 mb-6" data-id="element-8796">
            {searchTerm || selectedCategoryId !== 'all' ? 'Try adjusting your search or filter criteria' : 'Get started by adding your first product'}
          </p>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md inline-flex items-center transition-colors" data-id="element-8797">
            <PlusIcon size={16} className="mr-2" data-id="element-8798" />
            Add Product
          </button>
        </div>}
    </div>;
  // Render categories tab
  const renderCategoriesTab = () => <div className="space-y-4" data-id="element-8799">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex justify-between items-center" data-id="element-8800">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-8801">
          Product Categories
        </h3>
        <button className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium flex items-center" data-id="element-8802">
          <PlusIcon size={16} className="mr-1" data-id="element-8803" />
          Add Category
        </button>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-8804">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-8805">
          <thead className="bg-gray-50" data-id="element-8806">
            <tr data-id="element-8807">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8808">
                Category
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8809">
                Description
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8810">
                Products
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8811">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-8812">
            {categories.map(category => <tr key={category.id} className="hover:bg-gray-50" data-id="element-8813">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-8814">
                  <div className="flex items-center" data-id="element-8815">
                    <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-8816">
                      <TagIcon size={16} className="text-blue-600" data-id="element-8817" />
                    </div>
                    <div className="ml-4" data-id="element-8818">
                      <div className="text-sm font-medium text-gray-900" data-id="element-8819">
                        {category.name}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-8820">
                  <div className="text-sm text-gray-500" data-id="element-8821">
                    {category.description}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-8822">
                  <div className="text-sm text-gray-900" data-id="element-8823">
                    {category.productCount} products
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-8824">
                  <div className="flex justify-end space-x-2" data-id="element-8825">
                    <button className="text-gray-500 hover:text-blue-600" data-id="element-8826">
                      <EditIcon size={16} data-id="element-8827" />
                    </button>
                    <button className="text-gray-500 hover:text-red-600" data-id="element-8828">
                      <TrashIcon size={16} data-id="element-8829" />
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
      {/* Empty state */}
      {categories.length === 0 && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-8830">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-8831">
            <TagIcon size={24} className="text-blue-600" data-id="element-8832" />
          </div>
          <h3 className="text-lg font-medium text-gray-900" data-id="element-8833">
            No categories found
          </h3>
          <p className="text-gray-500 mt-2 mb-6" data-id="element-8834">
            Organize your products by creating categories
          </p>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md inline-flex items-center transition-colors" data-id="element-8835">
            <PlusIcon size={16} className="mr-2" data-id="element-8836" />
            Add Category
          </button>
        </div>}
    </div>;
  // Render settings tab
  const renderSettingsTab = () => <div className="space-y-4" data-id="element-8837">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-8838">
        <p className="text-center text-gray-600" data-id="element-8839">
          Shop-specific settings are available in the main Settings page under
          the "Shop Settings" tab.
        </p>
        <div className="flex justify-center mt-4" data-id="element-8840">
          <button onClick={() => window.location.href = '/settings?tab=shop-settings'} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium" data-id="element-8841">
            Go to Shop Settings
          </button>
        </div>
      </div>
    </div>;
  // Update the renderActiveTabContent function to include the dashboard tab
  const renderActiveTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboardTab();
      case 'products':
        return renderProductsTab();
      case 'categories':
        return renderCategoriesTab();
      case 'settings':
        return renderSettingsTab();
      default:
        return renderDashboardTab();
    }
  };
  return <div className="space-y-4" data-id="element-8842">
      {/* Back button and page title */}
      <div className="flex items-center" data-id="element-8843">
        <button onClick={navigateToShops} className="mr-4 p-2 rounded-md hover:bg-gray-100" data-id="element-8844">
          <ChevronLeftIcon size={20} className="text-gray-600" data-id="element-8845" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center" data-id="element-8846">
          <ShoppingBagIcon size={24} className="mr-2 text-blue-600" data-id="element-8847" />
          {isNewShop() ? 'Create New Shop' : shop?.name || 'Shop Details'}
        </h1>
      </div>
      {/* Main content */}
      <div className="space-y-4" data-id="element-8848">
        {isEditing || isNewShop() ? renderShopForm() : <>
            {renderShopHeader()}
            {renderTabs()}
            {renderActiveTabContent()}
          </>}
      </div>
    </div>;
}